import React, { useState, useCallback, useEffect } from 'react';
import {
  FileText,
  Download,
  Loader2,
  Truck,
  X,
  Check,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardHeader, CardContent } from '@components/Card';
import { PermissionGuard } from '@components/PermissionGuard';
import { DateInputBR } from '@components/DateInputBR';
import {
  shippingReportService,
  ShippingReportRow,
  ShippingReportSummary,
  ShippingReportFilters,
  ShippingProvider,
} from '../../services/shippingReportService';

// ─── Helpers ─────────────────────────────────────────────────────

const formatCurrency = (value?: number) =>
  value !== undefined && value !== null
    ? value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : 'R$ 0,00';

const formatWeight = (value?: number) =>
  value !== undefined && value !== null
    ? `${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg`
    : '0,00 kg';

const formatDocument = (doc?: string) => {
  if (!doc) return '—';
  if (doc.length === 14) {
    return doc.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  }
  if (doc.length === 11) {
    return doc.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  }
  return doc;
};

const formatCep = (cep?: string) => {
  if (!cep) return '—';
  if (cep.length === 8) return cep.replace(/^(\d{5})(\d{3})$/, '$1-$2');
  return cep;
};

const getToday = () => new Date().toISOString().split('T')[0];

const getFirstOfCurrentMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
};

// ─── CSV Export (client-side fallback) ───────────────────────────

// ─── Main Component ──────────────────────────────────────────────

export const ShippingReportView: React.FC = () => {
  // Filters
  const [dateFrom, setDateFrom] = useState(getFirstOfCurrentMonth);
  const [dateTo, setDateTo] = useState(getToday);
  const [selectedProviderIds, setSelectedProviderIds] = useState<number[]>([]);

  // Data
  const [rows, setRows] = useState<ShippingReportRow[]>([]);
  const [summary, setSummary] = useState<ShippingReportSummary | null>(null);
  const [providers, setProviders] = useState<ShippingProvider[]>([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState<'pdf' | 'excel' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  // Load providers on mount
  useEffect(() => {
    shippingReportService.getProviders().then(setProviders).catch(() => {});
  }, []);

  const buildFilters = useCallback((): ShippingReportFilters => ({
    date_from: dateFrom,
    date_to: dateTo,
    provider_ids: selectedProviderIds.length > 0 ? selectedProviderIds : undefined,
    page: 1,
    pageSize: 10000, // Fetch all data — report is grouped client-side
  }), [dateFrom, dateTo, selectedProviderIds]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await shippingReportService.getReport(buildFilters());
      setRows(result.data);
      setSummary(result.summary);
      setExpandedGroups({});
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar relatório');
      setRows([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [buildFilters]);

  const handleSearch = () => fetchReport();

  const toggleProvider = (id: number) => {
    setSelectedProviderIds(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  const toggleAllProviders = () => {
    if (selectedProviderIds.length === providers.length) {
      setSelectedProviderIds([]);
    } else {
      setSelectedProviderIds(providers.map(p => p.provider_id));
    }
  };

  const handleDownloadPdf = async () => {
    setExporting('pdf');
    try {
      await shippingReportService.downloadPdf({
        date_from: dateFrom,
        date_to: dateTo,
        provider_ids: selectedProviderIds.length > 0 ? selectedProviderIds : undefined,
      });
    } catch (err: any) {
      setError(err.message || 'Erro ao gerar PDF');
    } finally {
      setExporting(null);
    }
  };

  const handleDownloadExcel = async () => {
    setExporting('excel');
    try {
      await shippingReportService.downloadExcel({
        date_from: dateFrom,
        date_to: dateTo,
        provider_ids: selectedProviderIds.length > 0 ? selectedProviderIds : undefined,
      });
    } catch (err: any) {
      setError(err.message || 'Erro ao gerar Excel');
    } finally {
      setExporting(null);
    }
  };

  const toggleGroup = (key: string) => {
    setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Group data by company + provider
  const groupedData = React.useMemo(() => {
    const groups: Record<string, {
      company_document: string;
      company_name: string;
      provider_name: string;
      provider_id: number;
      rows: ShippingReportRow[];
      total_freight: number;
    }> = {};
    rows.forEach(row => {
      const key = `${row.company_document}_${row.freight_provider_id}`;
      if (!groups[key]) {
        groups[key] = {
          company_document: row.company_document,
          company_name: row.company_name,
          provider_name: row.freight_provider_name,
          provider_id: row.freight_provider_id,
          rows: [],
          total_freight: 0,
        };
      }
      groups[key].rows.push(row);
      groups[key].total_freight += row.freight_cost ?? 0;
    });
    return groups;
  }, [rows]);

  return (
    <PermissionGuard permission="reports.shipping">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Relatório de Transporte</h1>
            <p className="text-sm text-slate-500 mt-1">Análise de custos de frete por empresa e transportadora</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              icon={<FileText size={16} />}
              onClick={handleDownloadPdf}
              disabled={!summary || exporting !== null}
              loading={exporting === 'pdf'}
            >
              Imprimir PDF
            </Button>
            <Button
              variant="outline"
              icon={<Download size={16} />}
              onClick={handleDownloadExcel}
              disabled={!summary || exporting !== null}
              loading={exporting === 'excel'}
            >
              Exportar Excel
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader title="Filtros" />
          <CardContent className="space-y-4">
            {/* Date Range */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-2 uppercase tracking-wider">Período</label>
              <div className="flex flex-wrap items-center gap-3">
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Data inicial</label>
                  <DateInputBR
                    className="input text-sm"
                    value={dateFrom}
                    onChange={v => setDateFrom(v)}
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Data final</label>
                  <DateInputBR
                    className="input text-sm"
                    value={dateTo}
                    onChange={v => setDateTo(v)}
                  />
                </div>
              </div>
            </div>

            {/* Provider multi-select chips */}
            {providers.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs font-medium text-slate-600 uppercase tracking-wider">Transportadora</label>
                  <button
                    onClick={toggleAllProviders}
                    className="text-xs text-emerald-600 hover:underline"
                  >
                    {selectedProviderIds.length === providers.length ? 'Desmarcar todas' : 'Marcar todas'}
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {providers.map(p => {
                    const isSelected = selectedProviderIds.includes(p.provider_id);
                    return (
                      <button
                        key={p.provider_id}
                        onClick={() => toggleProvider(p.provider_id)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all cursor-pointer select-none ${
                          isSelected
                            ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                            : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                        }`}
                      >
                        {isSelected && <Check size={12} />}
                        {p.provider_name}
                      </button>
                    );
                  })}
                </div>
                {selectedProviderIds.length > 0 && (
                  <p className="text-xs text-slate-400 mt-2">
                    {selectedProviderIds.length} de {providers.length} selecionada(s)
                  </p>
                )}
              </div>
            )}

            {/* Search button */}
            <div className="flex justify-end pt-1">
              <Button
                onClick={handleSearch}
                disabled={loading}
                loading={loading}
              >
                Pesquisar
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
            <X size={18} className="text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
            <button className="ml-auto text-xs text-red-500 hover:underline" onClick={() => setError(null)}>Fechar</button>
          </div>
        )}

        {/* Summary Cards */}
        {summary && !loading && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white rounded-lg border border-slate-200 p-3">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Total Pedidos</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{summary.total_orders}</p>
            </div>
            <div className="bg-white rounded-lg border border-emerald-200 bg-emerald-50 p-3">
              <p className="text-xs text-emerald-600 uppercase tracking-wider font-medium">Custo Total Frete</p>
              <p className="text-2xl font-bold text-emerald-700 mt-1">{formatCurrency(summary.total_freight_cost)}</p>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 p-3">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Peso Total</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{formatWeight(summary.total_weight)}</p>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 p-3">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Valor Total Produtos</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{formatCurrency(summary.total_products_value)}</p>
            </div>
          </div>
        )}

        {/* Grouped Data Table */}
        {Object.keys(groupedData).length > 0 && !loading && (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                {Object.entries(groupedData).map(([key, group]) => (
                  <div key={key} className="border-b border-slate-200 last:border-b-0">
                    {/* Group header */}
                    <button
                      onClick={() => toggleGroup(key)}
                      className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        {expandedGroups[key] ? <ChevronDown size={16} className="text-slate-500 flex-shrink-0" /> : <ChevronRight size={16} className="text-slate-500 flex-shrink-0" />}
                        <div className="min-w-0">
                          <span className="font-semibold text-slate-900">{group.company_name}</span>
                          <span className="text-slate-500 text-sm ml-2">({formatDocument(group.company_document)})</span>
                          <span className="mx-2 text-slate-300">|</span>
                          <span className="text-sm text-slate-700">
                            <Truck size={13} className="inline mr-1" />
                            {group.provider_name}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 flex-shrink-0 ml-4">
                        <span className="text-xs text-slate-400">{group.rows.length} pedido(s)</span>
                        <span className="font-semibold text-emerald-700 text-sm">{formatCurrency(group.total_freight)}</span>
                      </div>
                    </button>

                    {/* Group rows */}
                    {expandedGroups[key] && (
                      <table className="w-full">
                        <thead className="table-header">
                          <tr>
                            <th className="py-2 px-6 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Pedido</th>
                            <th className="py-2 px-6 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">CEP Destino</th>
                            <th className="py-2 px-6 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">NF</th>
                            <th className="py-2 px-6 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">Volumes</th>
                            <th className="py-2 px-6 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">Peso</th>
                            <th className="py-2 px-6 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">Valor Produtos</th>
                            <th className="py-2 px-6 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">Valor Frete</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.rows.map(row => (
                            <tr key={row.order_id} className="table-row">
                              <td className="table-cell text-sm">#{row.order_id}</td>
                              <td className="table-cell text-sm">{formatCep(row.ship_addr_zip)}</td>
                              <td className="table-cell text-sm">{row.invoice_number || '—'}</td>
                              <td className="table-cell text-sm text-right">{row.volume_count}</td>
                              <td className="table-cell text-sm text-right">{formatWeight(row.total_weight)}</td>
                              <td className="table-cell text-sm text-right">{formatCurrency(row.order_products_total)}</td>
                              <td className="table-cell text-sm text-right font-medium">{formatCurrency(row.freight_cost)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Summary groups (from backend) — shown only if data table is empty but summary has groups */}
        {summary && summary.groups && summary.groups.length > 0 && Object.keys(groupedData).length === 0 && !loading && (
          <Card>
            <CardHeader title="Resumo por Empresa e Transportadora" />
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="table-header">
                  <tr>
                    <th className="py-2 px-6 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Empresa</th>
                    <th className="py-2 px-6 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">CNPJ</th>
                    <th className="py-2 px-6 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Transportadora</th>
                    <th className="py-2 px-6 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">Pedidos</th>
                    <th className="py-2 px-6 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">Total Frete</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.groups.map((g, idx) => (
                    <tr key={idx} className="table-row">
                      <td className="table-cell text-sm">{g.company_name}</td>
                      <td className="table-cell text-sm">{formatDocument(g.company_document)}</td>
                      <td className="table-cell text-sm">{g.provider_name}</td>
                      <td className="table-cell text-sm text-right">{g.total_orders}</td>
                      <td className="table-cell text-sm text-right font-semibold text-emerald-700">{formatCurrency(g.total_freight_cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {/* Empty state */}
        {!loading && rows.length === 0 && !error && !summary && (
          <Card>
            <CardContent className="py-12 text-center">
              <Truck size={48} className="mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-600 mb-1">Nenhum dado encontrado</h3>
              <p className="text-sm text-slate-400">Selecione os filtros e clique em pesquisar para gerar o relatório.</p>
            </CardContent>
          </Card>
        )}

        {/* Record count */}
        {!loading && rows.length > 0 && (
          <p className="text-sm text-slate-500">
            {rows.length} pedido(s) encontrado(s) em {Object.keys(groupedData).length} grupo(s)
          </p>
        )}

        {/* Loading overlay */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={32} className="animate-spin text-emerald-600" />
          </div>
        )}
      </div>
    </PermissionGuard>
  );
};

